import {
  parsePhoneNumber,
  getCountryCodeForRegionCode,
} from 'awesome-phonenumber';

const postalCodeRegex = {
  US: /^(\d{5})(?:[-\s](\d{4}))?$/,
  CA: /^(?!.*[DFIOQU])[A-VXY]\d[A-Z] \d[A-Z]\d$/,
};

export function checkPhoneNumber(value: string, countryCode = 'US') {
  const region = countryCode.toUpperCase();
  const pn = parsePhoneNumber(value, { regionCode: region });
  return pn.valid;
}

/**
 * NANP territories whose react-phone-input-2 dial code includes a 3-digit
 * area-code prefix (e.g. AS → "1684", AI → "1264"). When cleared, the field
 * retains 4 digits instead of the single ITU calling code digit "1".
 */
const NANP_TERRITORY_CODES = new Set([
  'AS',
  'AI',
  'AG',
  'BS',
  'BB',
  'BM',
  'VG',
  'KY',
  'DM',
  'GD',
  'GU',
  'JM',
  'MS',
  'MP',
  'KN',
  'LC',
  'VC',
  'SX',
  'TT',
  'TC',
  'VI',
]);

/**
 * Returns true when the phone input contains only the country dial code,
 * meaning the user has not typed any national number digits yet.
 *
 * react-phone-input-2 never fully empties — clearing retains the dial code:
 * - Most countries: digits equal the ITU calling code (e.g. "91" for India).
 * - NANP territories: 4 digits including the 3-digit area-code prefix that
 *   react-phone-input-2 appends (e.g. "1684" for American Samoa).
 */
export function isDialCodeOnly(
  phoneValue: string | undefined,
  countryCode: string | undefined
): boolean {
  const digits = (phoneValue || '').replaceAll(/\D/g, '');
  if (!digits) return true;

  // Default to the same region as the phone input (US) when countryCode is
  // missing, so partially-entered numbers like "+1 2" are not treated as
  // "empty" for an unknown region.
  const upperCode = (countryCode || 'US').toUpperCase();

  if (NANP_TERRITORY_CODES.has(upperCode)) {
    return digits.length <= 4;
  }

  const callingCode = getCountryCodeForRegionCode(upperCode);
  if (!callingCode) {
    // If we cannot resolve a calling code for this region, do not treat
    // short digit sequences as "dial-code-only".
    return false;
  }
  return digits === String(callingCode);
}

export function checkPostalCode(
  postalCode: string,
  countryCode = 'US',
  postalCodeRegexRules = postalCodeRegex
): boolean {
  return postalCodeRegexRules[countryCode].test(postalCode);
}

export function checkCallbackURL(value: string): boolean {
  return /^https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?$/.test(value);
}
